/**
 * @fileoverview RecentDealsSlideout component
 * Wrapper component for the slideout panel that displays recent deals
 * Manages slideout visibility, positioning, and loading states
 * Provides hide functionality through Redux dispatch
 */

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { hideRecentDeals } from '../../store';
import { Spinner } from '@interstate/components/Spinner';
import { SLIDEOUT_POSITION, SLIDEOUT_PANEL_WIDTH } from '../../constants';
import { RecentDealsFilter, StyledSlideout } from './RecentDealsSlideout.style';
import { StyledWrapper } from '../RecentDealsList/RecentDealsList.style';
import type { RecentDealsSlideoutProps } from '../../types/RecentDealsProps';

/**
 * RecentDealsSlideout component
 * Renders the slideout panel container for recent deals:
 * - Displays a spinner when isLoading is true
 * - Shows header section with title and actions
 * - Renders footer with configurable buttons
 * - Handles slideout hide action via Redux
 * - Configures slideout position and panel width
 *
 * @param isLoading - Flag indicating whether data is being fetched
 * @param header - Header content to display at the top of the slideout
 * @param footer - Footer configuration with button actions
 * @param children - Child content to render in the slideout body
 * @returns Styled slideout panel with header, content, and footer
 */
const RecentDealsSlideout: React.FC<RecentDealsSlideoutProps> = ({ isLoading, header, filter, footer, children }) => {
    const dispatch = useDispatch();
    const handleHideDeals = useCallback(() => {
        dispatch(hideRecentDeals());
    }, [dispatch]);

    return (
        <StyledSlideout
            show={true}
            data-testId="recent-deals-drawer"
            className="recent-deals-slideout"
            position={SLIDEOUT_POSITION}
            header={header}
            onHide={handleHideDeals}
            panelWidth={SLIDEOUT_PANEL_WIDTH}
            footer={footer}
        >
            {filter && <RecentDealsFilter>{filter}</RecentDealsFilter>}
            {isLoading ? (
                <StyledWrapper>
                    <Spinner />
                </StyledWrapper>
            ) : (
                children
            )}
        </StyledSlideout>
    );
};

export default RecentDealsSlideout;
